// apps/app/src/coachmark/CoachmarkHost.tsx
// Portal-mounted surface that looks up the open coachmark's target via
// querySelector, computes the popover's position from the target's
// bounding rect, and re-renders on scroll/resize. Hides the popover
// when the target is missing.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Coachmark } from './Coachmark.js';
import { type CoachmarkId, coachmarkContent } from './content.js';
import type { CoachmarkController } from './useCoachmarks.js';

interface CoachmarkHostProps {
  readonly controller: CoachmarkController;
}

interface Position {
  readonly left: number;
  readonly top: number;
}

/** Either beside the target, or docked bottom-right of the map area. */
export type CoachmarkPosition = Position | { readonly dock: 'bottom-right' };

/** Gap between the target's right edge and the popover's left edge (px). */
export const OFFSET_PX = 18;
/** Popover max width (Coachmark's max-w) and the margin kept from the viewport edge. */
export const POPOVER_WIDTH_PX = 320;
export const VIEWPORT_MARGIN_PX = 8;

/**
 * Popover position for a target rect. Prefers the right of the target; when
 * that would run past the viewport edge (narrow / mobile screens, targets at
 * the right edge) it flips below the target, clamped inside the viewport.
 */
export function computePosition(
  rect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>,
  viewport: { width: number; height: number } = {
    width: window.innerWidth,
    height: window.innerHeight,
  },
): Position {
  const right = rect.left + rect.width + OFFSET_PX;
  if (right + POPOVER_WIDTH_PX <= viewport.width - VIEWPORT_MARGIN_PX) {
    return { left: right, top: rect.top };
  }
  const width = Math.min(POPOVER_WIDTH_PX, viewport.width - VIEWPORT_MARGIN_PX * 2);
  const left = Math.min(
    Math.max(VIEWPORT_MARGIN_PX, rect.left),
    viewport.width - VIEWPORT_MARGIN_PX - width,
  );
  return { left, top: rect.top + rect.height + OFFSET_PX / 2 };
}

export function CoachmarkHost({ controller }: CoachmarkHostProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [position, setPosition] = useState<CoachmarkPosition | null>(null);

  useEffect(() => {
    function tick() {
      let found: string | null = null;
      for (const id of Object.keys(coachmarkContent) as CoachmarkId[]) {
        if (controller.isOpen(id)) {
          found = id;
          break;
        }
      }
      // The audience gate re-evaluates every tick, not only when the hint
      // opens: the gate reads the live DOM, and what it hides can appear
      // AFTER the hint opened — on the continue menu the save (and with it
      // the Continue button) loads asynchronously, so the sequence adapter's
      // open-time check can run while the gate's selector is still absent.
      // Closing here (rather than merely hiding) lets the sequence advance.
      if (found) {
        const entry = coachmarkContent[found as CoachmarkId];
        if (entry.hideWhenSelector && document.querySelector(entry.hideWhenSelector)) {
          controller.closeHint(found);
          found = null;
        }
      }
      setOpenId((cur) => (cur === found ? cur : found));
    }
    tick();
    const id = window.setInterval(tick, 100);
    return () => window.clearInterval(id);
  }, [controller]);

  useEffect(() => {
    if (!openId) {
      setPosition(null);
      return;
    }
    // Capture once after the narrowing check so the closure below sees
    // `id: string` (not `string | null`) — TS doesn't narrow through the
    // inner `update` closure on its own.
    const id = openId;
    const entry = coachmarkContent[id as CoachmarkId];
    // The audience gate: an entry with `hideWhenSelector` closes when its
    // selector matches (e.g. the start-game hint never shows over the
    // Continue button — that is a returning player, and the card would cover
    // the menu's CTA stack at phone sizes). Closing (not merely hiding) lets
    // the sequence adapter advance, same as a vanished anchor.
    if (entry.hideWhenSelector && document.querySelector(entry.hideWhenSelector)) {
      controller.closeHint(id);
      setPosition(null);
      return;
    }
    const target = document.querySelector(entry.targetSelector);
    if (!target) {
      // The anchor for this hint has unmounted (e.g. the menu Start Game
      // button disappears once the player clicks into the match). The hint
      // is now "open but invisible" — if we leave it that way the sequence
      // adapter's `controller.isOpen(id) → continue` short-circuit blocks
      // every subsequent hint. Close the controller-side open state so the
      // sequence can advance to the next trigger.
      controller.closeHint(id);
      setPosition(null);
      return;
    }
    function update() {
      if (entry.hideWhenSelector && document.querySelector(entry.hideWhenSelector)) {
        controller.closeHint(id);
        setPosition(null);
        return;
      }
      const t = document.querySelector(entry.targetSelector);
      if (!t) {
        // Same rationale as above: the anchor disappeared mid-render. Close
        // so the sequence can move on.
        controller.closeHint(id);
        setPosition(null);
        return;
      }
      setPosition(
        entry.placement === 'bottom-right'
          ? { dock: 'bottom-right' }
          : computePosition(t.getBoundingClientRect()),
      );
    }
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [openId, controller]);

  if (!openId || !position) return null;
  const entry = coachmarkContent[openId as CoachmarkId];
  return createPortal(
    <Coachmark
      key={openId}
      hintId={entry.id}
      title={entry.title}
      body={entry.body}
      learnMoreUrl={entry.learnMoreUrl}
      position={position}
      onDismiss={() => controller.closeHint(openId)}
    />,
    document.body,
  );
}
